import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Phone,
  Package,
  Globe,
  Clock,
  Shield,
  Users,
  MapPin,
  Mail,
  ArrowRight,
  FileText,

  CheckCircle,
  Send,
  Plane,
  Truck,
  ChevronDown,
  Star
} from 'lucide-react';

const WhatsAppIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const Home = () => {

  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    pickupCity: '',
    destinationCountry: '',
    shipmentType: ''
  });
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);



  /* ── Lead Form ── */
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Redirect to WhatsApp
    const whatsappUrl = getWhatsAppLink();
    window.open(whatsappUrl, '_blank');

    // Show success message
    setIsFormSubmitted(true);
    setTimeout(() => {
      setIsFormSubmitted(false);
      setFormData({
        name: '',
        mobile: '',
        pickupCity: '',
        destinationCountry: '',
        shipmentType: ''
      });
    }, 3000);
  };

  const scrollToForm = () => {
    document.getElementById('quote-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const getWhatsAppLink = () => {
    const msg = `Hi, I need a quote for international courier.\n\nName: ${formData.name}\nMobile: ${formData.mobile}\nPickup City: ${formData.pickupCity}\nDestination: ${formData.destinationCountry}\nShipment Type: ${formData.shipmentType}`;
    return `https://wa.me/918847428801?text=${encodeURIComponent(msg)}`;
  };

  /* ── Data ── */
  const services = [
    {
      icon: <FileText />,
      title: 'Document Courier',
      description: 'Send important documents like certificates, passports, legal papers, and business documents safely to any country with fast delivery and full tracking.',
      color: '#3B82F6'
    },
    {
      icon: <Package />,
      title: 'Parcel Courier',
      description: 'Ship personal parcels, gifts, household items, and online purchases internationally with secure packing and affordable rates.',
      color: '#10B981'
    },
    {
      icon: <Shield />,
      title: 'Medicine Courier',
      description: 'Reliable international medicine delivery with proper handling and temperature care. We follow all customs and pharmaceutical regulations.',
      color: '#EF4444'
    },
    {
      icon: <Truck />,
      title: 'Food Items Courier',
      description: 'Send homemade food, pickles, spices, sweets, and dry food items to your loved ones abroad with safe and hygienic packing.',
      color: '#F59E0B'
    },
    {
      icon: <Globe />,
      title: 'Commercial Shipment',
      description: 'Bulk and commercial international shipments for businesses. Complete customs documentation, clearance support, and competitive B2B rates.',
      color: '#8B5CF6'
    },
    {
      icon: <Plane />,
      title: 'Excess Baggage',
      description: 'Relocating abroad? Ship your excess baggage and personal belongings at much lower rates than airline excess baggage charges.',
      color: '#06B6D4'
    }
  ];

  const countries = [
    { flag: 'https://flagcdn.com/w160/us.png', name: 'USA', delivery: '5–7 Days' },
    { flag: 'https://flagcdn.com/w160/ca.png', name: 'Canada', delivery: '5–7 Days' },
    { flag: 'https://flagcdn.com/w160/gb.png', name: 'United Kingdom', delivery: '4–6 Days' },
    { flag: 'https://flagcdn.com/w160/au.png', name: 'Australia', delivery: '5–8 Days' },
    { flag: 'https://flagcdn.com/w160/ae.png', name: 'Dubai / UAE', delivery: '3–5 Days' },
    { flag: 'https://flagcdn.com/w160/eu.png', name: 'Europe', delivery: '5–7 Days' }
  ];

  const whyChooseUs = [
    { icon: <Star />, title: 'Competitive Prices', description: 'Best rates for international courier from Chandigarh. No hidden charges, transparent pricing for every shipment.' },
    { icon: <Clock />, title: 'Fast Delivery', description: 'Express and standard delivery options. Most shipments delivered within 4–7 business days worldwide.' },
    { icon: <Shield />, title: 'Safe & Secure Packing', description: 'Professional multi-layer packing to ensure your items reach safely. Special care for fragile and sensitive items.' },
    { icon: <Users />, title: 'Experienced Team', description: 'Our logistics experts handle customs paperwork, documentation, and international shipping regulations seamlessly.' },
    { icon: <MapPin />, title: 'Doorstep Pickup', description: 'Free doorstep pickup from Chandigarh, Mohali, Panchkula, and Zirakpur. No need to visit any office.' },
    { icon: <Phone />, title: 'Customer Support', description: 'Dedicated support team available to help you track shipments and resolve queries quickly.' }
  ];

  const steps = [
    { icon: <Phone />, title: 'Contact Us', description: 'Call, WhatsApp, or fill the enquiry form to get an instant quote for your shipment.' },
    { icon: <MapPin />, title: 'Pickup from Your Location', description: 'Our executive will come to your doorstep in Chandigarh, Mohali, Panchkula, or Zirakpur for pickup.' },
    { icon: <Package />, title: 'Packing & Customs Clearance', description: 'We professionally pack your items and handle all customs documentation and clearance.' },
    { icon: <Plane />, title: 'Fast International Delivery', description: 'Your shipment is dispatched via trusted international carriers and delivered to the destination.' }
  ];

  const faqs = [
    {
      question: 'How are international courier charges calculated?',
      answer: 'International courier charges are based on the weight of the shipment (actual or volumetric, whichever is higher), destination country, and type of shipment. We offer competitive rates starting from affordable prices. Contact us for an exact quote.'
    },
    {
      question: 'How many days does delivery take?',
      answer: 'Delivery typically takes 3–7 business days depending on the destination country. Express delivery to Dubai/UAE takes 3–5 days, UK takes 4–6 days, and USA/Canada/Australia takes 5–8 days. You will receive tracking details to monitor your shipment.'
    },
    {
      question: 'Is medicine courier allowed internationally?',
      answer: 'Yes, we do courier medicines internationally. However, medicines must be properly prescribed, sealed in original packaging, and accompanied by a valid prescription. Some countries have specific regulations which our team will guide you through.'
    },
    {
      question: 'Will I get tracking details?',
      answer: 'Absolutely! Every shipment gets a unique tracking number. You can track your parcel in real-time from pickup to delivery. We also send regular updates via WhatsApp and SMS.'
    },
    {
      question: 'Is home pickup available in my area?',
      answer: 'Yes! We provide free doorstep pickup from Chandigarh, Mohali, Panchkula, Zirakpur, and surrounding areas. Just call or WhatsApp us and our executive will visit your location at your preferred time.'
    }
  ];

  return (
    <div className="home">

      {/* ===== 1. HERO SECTION ===== */}
      <section className="hero">
        <div className="hero-content">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-text"
          >
            <h1>International Courier Service from Chandigarh to Worldwide</h1>
            <p className="hero-subheading">Fast, Safe &amp; Affordable International Shipping</p>
            <p className="hero-description">
              Send documents, parcels, medicines, food items &amp; commercial shipments to USA, Canada, UK, Australia, Dubai &amp; 50+ countries. Free doorstep pickup from Chandigarh, Mohali, Panchkula &amp; Zirakpur.
            </p>

            <div className="hero-buttons">
              <a href="tel:+918847428801" className="btn btn-primary">
                <Phone size={18} />
                Call Now
              </a>
              <a
                href="https://wa.me/918847428801?text=Hi%2C%20I%20need%20a%20quote%20for%20international%20courier%20from%20Chandigarh."
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-whatsapp"
              >
                <WhatsAppIcon size={18} />
                WhatsApp Now
              </a>
              <button onClick={scrollToForm} className="btn btn-accent">
                Get Free Quote
                <ArrowRight size={18} />
              </button>
              <button
                onClick={() => {
                  window.location.hash = '#track-shipment';
                }}
                className="btn btn-track-hero"
              >
                <Package size={18} />
                Track Shipment
              </button>
            </div>

            {/* Trust Points */}
            <div className="trust-points">
              <div className="trust-item"><CheckCircle size={16} /><span>Door to Door Pickup</span></div>
              <div className="trust-item"><CheckCircle size={16} /><span>Secure Packing</span></div>
              <div className="trust-item"><CheckCircle size={16} /><span>Customs Clearance</span></div>
              <div className="trust-item"><CheckCircle size={16} /><span>Tracking Available</span></div>
            </div>


          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hero-image"
          >
            <div className="hero-visual">
              <img
                src="/images/hero_split.jpg"
                alt="International Courier Service from Chandigarh"
                className="hero-main-image"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.nextElementSibling?.classList.remove('hidden');
                }}
              />
              <div className="hero-visual-fallback hidden">
                <Plane className="hero-truck" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== 2. LEAD / ENQUIRY FORM ===== */}
      <section id="quote-form" className="lead-form-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2>Get Instant Quote</h2>
            <p>Fill in your details and we'll get back to you with the best rates</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lead-form-wrapper"
          >
            {isFormSubmitted ? (
              <div className="form-success">
                <CheckCircle size={48} />
                <h3>Thank You!</h3>
                <p>Our team will contact you shortly with the best rates. For instant response:</p>
                <a
                  href={getWhatsAppLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-whatsapp"
                >
                  <WhatsAppIcon size={18} />
                  Chat on WhatsApp
                </a>
              </div>
            ) : (
              <form
                name="lead-form"
                method="POST"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                onSubmit={handleFormSubmit}
                className="lead-form"
              >
                <input type="hidden" name="form-name" value="lead-form" />
                <input type="hidden" name="bot-field" />

                <div className="lead-form-grid">
                  <div className="form-group">
                    <label htmlFor="lead-name">Your Name</label>
                    <input
                      type="text"
                      id="lead-name"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      placeholder="Enter your name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="lead-mobile">Mobile Number *</label>
                    <input
                      type="tel"
                      id="lead-mobile"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleFormChange}
                      placeholder="Enter 10-digit mobile number"
                      required
                      pattern="[0-9]{10}"
                      title="Please enter a valid 10-digit mobile number"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="lead-city">Pickup City</label>
                    <select id="lead-city" name="pickupCity" value={formData.pickupCity} onChange={handleFormChange} required>
                      <option value="">Select Pickup City</option>
                      <option value="Chandigarh">Chandigarh</option>
                      <option value="Mohali">Mohali</option>
                      <option value="Panchkula">Panchkula</option>
                      <option value="Zirakpur">Zirakpur</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="lead-country">Destination Country</label>
                    <select id="lead-country" name="destinationCountry" value={formData.destinationCountry} onChange={handleFormChange} required>
                      <option value="">Select Destination</option>
                      <option value="USA">USA</option>
                      <option value="Canada">Canada</option>
                      <option value="UK">United Kingdom</option>
                      <option value="Australia">Australia</option>
                      <option value="Dubai/UAE">Dubai / UAE</option>
                      <option value="Germany">Germany</option>
                      <option value="Europe (Other)">Europe (Other)</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="lead-type">Shipment Type</label>
                    <select id="lead-type" name="shipmentType" value={formData.shipmentType} onChange={handleFormChange} required>
                      <option value="">Select Type</option>
                      <option value="Document">Document</option>
                      <option value="Parcel">Parcel</option>
                      <option value="Commercial">Commercial</option>
                    </select>
                  </div>

                  <div className="form-group form-submit-group" style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center' }}>
                    <button type="submit" className="btn btn-primary btn-lg">
                      <Send size={18} />
                      Get Instant Quote
                    </button>
                  </div>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* ===== 3. SERVICES ===== */}
      <section id="services" className="services-overview">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2>Our International Courier Services</h2>
            <p>Comprehensive international shipping solutions from Chandigarh</p>
          </motion.div>

          <div className="services-grid">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="service-card"
                style={{ '--accent-color': service.color } as React.CSSProperties}
              >
                <div className="service-icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
              </motion.div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <Link to="/services" className="btn btn-outline">
              View All Services <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== 4. COUNTRIES WE SERVE ===== */}
      <section className="countries-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2>Countries We Serve</h2>
            <p>Reliable international courier delivery to major destinations worldwide</p>
          </motion.div>

          <div className="countries-grid">
            {countries.map((country, index) => (
              <motion.div
                key={country.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="country-card"
              >
                <img src={country.flag} alt={`${country.name} flag`} className="country-flag" />
                <h3>{country.name}</h3>
                <p className="country-delivery">
                  <Clock size={14} />
                  {country.delivery}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="countries-description"
          >
            We provide fast, affordable, and secure international courier services from Chandigarh
            to USA, Canada, United Kingdom, Australia, Dubai, and all European countries. Whether
            you're sending personal documents, parcels, gifts, food items, or commercial shipments,
            our experienced team ensures safe delivery with complete tracking and customs clearance
            support. We partner with leading global carriers to offer the best rates and fastest
            delivery times from the Chandigarh tri-city area.
          </motion.p>
        </div>
      </section>

      {/* ===== 5. WHY CHOOSE US ===== */}
      <section className="why-choose-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2>Why Choose Us</h2>
            <p>Trusted by hundreds of customers in Chandigarh for international shipping</p>
          </motion.div>

          <div className="why-grid">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="why-card"
              >
                <div className="why-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 6. HOW IT WORKS ===== */}
      <section className="how-it-works-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2>How It Works</h2>
            <p>Simple 4-step process to ship your items internationally</p>
          </motion.div>

          <div className="steps-grid">
            {steps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="step-card"
              >
                <div className="step-number">{index + 1}</div>
                <div className="step-icon">{step.icon}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 7. LOCAL AREA / SEO SECTION ===== */}
      <section className="local-seo-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2>International Courier Service in Chandigarh</h2>
            <div className="local-seo-content">
              <div className="local-seo-text">
                <p>
                  Looking for a reliable <strong>international courier service in Chandigarh</strong>?
                  Pack & Fly is your trusted partner for sending documents, parcels, food items,
                  medicines, and commercial shipments from Chandigarh to any country in the world. We provide
                  free doorstep pickup across the Chandigarh tri-city area including <strong>Mohali</strong>,
                  <strong> Panchkula</strong>, and <strong>Zirakpur</strong>.
                </p>
                <p>
                  Our services cover all major sectors and residential areas in the region. Whether you are
                  in Sector 17, Industrial Area Phase 1, IT Park Mohali, Manimajra, Kharar, Dera Bassi, or
                  any other locality &mdash; our pickup executive will come to your location at your preferred time.
                  We offer the most competitive rates with fastest delivery times for <strong>international
                    courier from Chandigarh</strong>.
                </p>
                <p>
                  With years of experience in international logistics, we understand the customs regulations,
                  documentation requirements, and shipping standards for all major countries. Our team handles
                  everything from professional packing to customs clearance, making the shipping process
                  completely hassle-free for you.
                </p>
              </div>
              <div className="local-seo-areas">
                <h3>Service Areas</h3>
                <div className="area-tags">
                  {['Chandigarh', 'Mohali', 'Panchkula', 'Zirakpur', 'Kharar', 'Dera Bassi', 'Manimajra', 'Sector 17', 'IT Park Mohali', 'Industrial Area'].map(area => (
                    <span key={area} className="area-tag">
                      <MapPin size={14} />
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== 8. FAQ SECTION ===== */}
      <section id="faq" className="faq-section-landing">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2>Frequently Asked Questions</h2>
            <p>Common questions about our international courier services</p>
          </motion.div>

          <div className="faq-accordion">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`faq-accordion-item ${openFaq === index ? 'open' : ''}`}
              >
                <button
                  className="faq-question"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span>{faq.question}</span>
                  <ChevronDown size={20} className={`faq-chevron ${openFaq === index ? 'rotated' : ''}`} />
                </button>
                {openFaq === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="faq-answer"
                  >
                    <p>{faq.answer}</p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 9. CONTACT SECTION ===== */}
      <section id="contact" className="contact-landing-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2>Contact Us</h2>
            <p>Get in touch for the best international courier rates from Chandigarh</p>
          </motion.div>

          <div className="contact-landing-grid">
            <div className="contact-details">
              <div className="contact-detail-item">
                <Phone size={20} />
                <div>
                  <h4>Phone</h4>
                  <a href="tel:+918847428801">+91 8847428801</a>
                </div>
              </div>

              <div className="contact-detail-item">
                <WhatsAppIcon size={20} />
                <div>
                  <h4>WhatsApp</h4>
                  <a href="https://wa.me/918847428801" target="_blank" rel="noopener noreferrer">+91 8847428801</a>
                </div>
              </div>

              <div className="contact-detail-item">
                <Mail size={20} />
                <div>
                  <h4>Email</h4>
                  <a href="mailto:packnfly96@gmail.com">packnfly96@gmail.com</a>
                </div>
              </div>

              <div className="contact-detail-item">
                <MapPin size={20} />
                <div>
                  <h4>Address</h4>
                  <p>Pack N Fly<br />Plot No 5A, Industrial Area<br />Phase 2, Panchkula, Haryana</p>
                </div>
              </div>

              <div className="contact-detail-item">
                <Clock size={20} />
                <div>
                  <h4>Business Hours</h4>
                  <p>Monday &ndash; Saturday: 9:00 AM &ndash; 7:00 PM</p>
                </div>
              </div>
            </div>

            <div className="contact-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d109741.02005536151!2d76.68839365!3d30.7363!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390fed0be66ec96b%3A0xa5ff67f9527319fe!2sChandigarh!5e0!3m2!1sen!2sin!4v1707200000000"
                width="100%"
                height="400"
                style={{ border: 0, borderRadius: '1rem' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Service Area - Chandigarh"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="services-cta">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="cta-content"
          >
            <h2>Ready to Ship Internationally?</h2>
            <p>Get the best international courier rates from Chandigarh. Call or WhatsApp us now!</p>
            <div className="cta-buttons">
              <a href="tel:+918847428801" className="btn btn-primary">
                <Phone size={18} />
                Call: +91 8847428801
              </a>
              <a
                href="https://wa.me/918847428801?text=Hi%2C%20I%20need%20a%20quote%20for%20international%20courier%20from%20Chandigarh."
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-whatsapp"
              >
                <WhatsAppIcon size={18} />
                WhatsApp Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== FLOATING WHATSAPP BUTTON ===== */}
      <a
        href="https://wa.me/918847428801?text=Hi%2C%20I%20need%20a%20quote%20for%20international%20courier%20from%20Chandigarh."
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float"
        aria-label="Chat on WhatsApp"
      >
        <WhatsAppIcon size={28} />
      </a>


    </div>
  );
};

export default Home;
